import type {
  DeleteDocumentParams,
  DeleteSubDocumentParams,
  DocumentReturn,
  WriteDocumentParams,
  WriteSubDocumentParams,
} from './types';

import { isNewEntity } from '@snowit/constants';
import useFirebase from '@snowit/firebase';

import useLanguage from '../useLanguage';
import { clean } from './utils';

export default () => {
  const { t } = useLanguage();
  const firebase = useFirebase();

  const getMessage = (type: 'write' | 'delete', model: string, document?: string) => {
    const isNew = isNewEntity(document);
    return t(['lzy.notify.', type, isNew ? '_new' : ''].filter(Boolean).join(''), {
      document,
      model,
    });
  };

  const writeDocument = async (params: WriteDocumentParams): Promise<string | undefined> => {
    try {
      const attributes = clean<WriteDocumentParams['attributes']>({
        ...params.attributes,
      });
      const res: DocumentReturn = await firebase.call(
        'models-saveDocument',
        {
          modelName: params.modelName,
          attributes,
          id: params.id,
        },
        {
          message: getMessage('write', params.modelName, params.id),
          blockNotify: params._blockNotify,
        },
      );
      return res?.data?.id;
    } catch {
      return undefined;
    }
  };

  const writeSubDocument = async (params: WriteSubDocumentParams): Promise<string | undefined> => {
    try {
      const res: DocumentReturn = await firebase.call(
        'models-saveDocument',
        {
          modelName: params.modelName,
          attributes: clean<WriteSubDocumentParams['attributes']>(params.attributes),
          parentId: params.parentId,
          id: params.id,
        },
        {
          message: getMessage('write', params.modelName, params.id),
          blockNotify: params._blockNotify,
        },
      );

      return res?.data?.id;
    } catch {
      return undefined;
    }
  };

  const deleteDocument = async (params: DeleteDocumentParams): Promise<boolean> => {
    try {
      await firebase.call(
        'models-deleteDocument',
        {
          modelName: params.modelName,
          id: params.id,
        },
        {
          message: getMessage('delete', params.modelName, params.id),
          blockNotify: params._blockNotify,
        },
      );
      return true;
    } catch {
      return false;
    }
  };

  const deleteSubDocument = async (params: DeleteSubDocumentParams): Promise<boolean> => {
    try {
      await firebase.call(
        'models-deleteDocument',
        {
          modelName: params.modelName,
          id: params.id,
          parentId: params.parentId,
        },
        {
          message: getMessage('delete', params.modelName, params.id),
          blockNotify: params._blockNotify,
        },
      );
      return true;
    } catch {
      return false;
    }
  };

  return {
    writeDocument,
    writeSubDocument,
    deleteDocument,
    deleteSubDocument,
  };
};
